<template>
    <div class="hotspot-select-screen-box" :style="{ 'background-image': `url(${question.image})` }">
        <div
            :class="{ 'hotspot-tool flex': true, 'justify-between w-full': isMobile }"
            :style="[isMobile ? { top: '-34px', right: '0', 'column-gap': '6px' } : {}]"
        >
            <div class="flex items-center" style="column-gap: 6px">
                <template v-if="isMobile && selectedData">
                    <div
                        :class="{
                            'btn-success': selectedData && selectedData.is_correct,
                            'hotspot-tool-box flex justify-center items-center cursor-pointer shadow-text-none': true
                        }"
                        @click="selectCorrect(selectedData)"
                    >
                        <i class="fas fa-check hotspot-check-icon"></i>
                    </div>
                    <div
                        v-if="isMobile && selectedData"
                        class="hotspot-tool-box flex justify-center items-center cursor-pointer shadow-text-none"
                        @click="removeHotspotBox(selectedData.id)"
                    >
                        <i class="fas fa-trash-alt hotspot-trash-icon"></i>
                    </div>
                    <span :style="[colorCode ? { color: colorCode } : '#000']">{{ selectedData.name }}</span>
                </template>
            </div>
            <div
                :class="{ 'flex d-flex': true, 'flex-col flex-column': !isMobile }"
                :style="[isMobile ? { 'column-gap': '6px' } : { 'row-gap': '6px' }]"
            >
                <div
                    class="
                        hotspot-tool-box
                        btn-dark
                        flex
                        d-flex
                        justify-center justify-content-center
                        items-center
                        align-items-center
                        cursor-pointer
                        shadow-text-none
                    "
                    @click="addHotspotBox('rect')"
                >
                    <div class="rect-icon"></div>
                </div>
                <div
                    class="
                        hotspot-tool-box
                        btn-dark
                        flex
                        d-flex
                        justify-center justify-content-center
                        items-center
                        align-items-center
                        cursor-pointer
                        shadow-text-none
                    "
                    @click="addHotspotBox('circle')"
                >
                    <div class="circle-icon"></div>
                </div>
                <div
                    v-if="showRemoveImageButton"
                    class="
                        hotspot-tool-box
                        btn-danger
                        flex
                        d-flex
                        justify-center justify-content-center
                        items-center
                        align-items-center
                        cursor-pointer
                        shadow-text-none
                    "
                    @click="removeImage()"
                >
                    <i class="fas fa-times"></i>
                </div>
            </div>
        </div>

        <span
            v-if="!isMobile && selectedData"
            class="shadow-text-none"
            :style="[colorCode ? { color: colorCode } : '#000']"
            style="position: absolute; top: -24px; left: 0"
            >{{ selectedData.name }}</span
        >

        <v-stage
            class="hotspot-select-screen w-full h-full"
            ref="stage"
            :config="stageSize"
            @mousedown="handleStageMouseDown"
            @touchstart="handleStageMouseDown"
        >
            <v-layer ref="layer">
                <v-image
                    :config="{
                        image: image,
                        width: stageSize.width,
                        height: stageSize.height
                    }"
                />
                <v-group
                    v-for="item in hotspotBox"
                    :key="item.id"
                    :config="item.group"
                    :ref="item.id"
                    @dragmove="handleDrag"
                    @dragend="!isMobile ? tooltipDisable(false) : false"
                >
                    <v-rect
                        v-if="!item.type || item.type == 'rect'"
                        :config="item.rectangles"
                        @transform="handleTransform"
                        @transformend="!isMobile ? tooltipDisable(false) : false"
                    />

                    <v-circle
                        v-if="item.type == 'circle'"
                        :config="item.rectangles"
                        @transform="handleTransform"
                        @transformend="!isMobile ? tooltipDisable(false) : false"
                    />

                    <template v-if="!isMobile">
                        <v-label
                            :ref="`${item.id}-bin`"
                            :config="{
                                x:
                                    item.type == 'circle'
                                        ? item.rectangles.x + 4 - item.rectangles.x - 50
                                        : item.rectangles.x + 4,
                                y:
                                    item.type == 'circle'
                                        ? item.rectangles.y + 4 - item.rectangles.y - 50
                                        : item.rectangles.y + 4
                            }"
                            @click="removeHotspotBox(item.id)"
                        >
                            <v-image :config="{ image: binImage, width: 20, height: 20 }" />
                        </v-label>
                        <v-label
                            :ref="`${item.id}-check`"
                            :config="{
                                x:
                                    item.type == 'circle'
                                        ? item.rectangles.x + 28 - item.rectangles.x - 50
                                        : item.rectangles.x + 28,
                                y:
                                    item.type == 'circle'
                                        ? item.rectangles.y + 4 - item.rectangles.y - 50
                                        : item.rectangles.y + 4
                            }"
                            @click="selectCorrect(item)"
                        >
                            <v-image
                                :config="{
                                    image: item.is_correct ? checkedImage : unCheckedImage,
                                    width: 20,
                                    height: 20
                                }"
                            />
                        </v-label>
                    </template>
                </v-group>
                <v-transformer
                    ref="transformer"
                    :config="{
                        rotateEnabled: false,
                        flipEnabled: false,
                        keepRatio: false,
                        boundBoxFunc: boundBoxFunction
                    }"
                />
            </v-layer>
        </v-stage>
    </div>
</template>

<script>
import helperMixins from '@/mixins/helper.js'

let baseUrl, popup

if (typeof process.env.VUE_APP_IS_BACKEND !== 'undefined') {
    baseUrl = process.env.VUE_APP_API_BASE_URL.replace('/admin/api', '/')
} else {
    baseUrl = process.env.VUE_APP_API_BASE_URL.replace('/api', '')
    popup = require('@/lib/popup.js').default
}

export default {
    props: {
        question: {
            type: Object,
            required: true
        },

        showRemoveImageButton: {
            type: Boolean,
            default: true
        },

        colorCode: {
            type: String,
            default: '#000'
        }
    },

    mixins: [helperMixins],

    data() {
        return {
            hotspotBox: [],
            idCount: null,
            stageSize: {
                width: 500,
                height: 500
            },
            image: null,
            selectedShapeName: '',
            checkedImage: null,
            unCheckedImage: null,
            binImage: null,
            isMobile: false,
            removeList: []
        }
    },

    created() {
        this.renderCanvasImage()
        this.checkIsMobile()
        this.hotspotBox = this.question.answers.filter(e => e.id)

        if (this.hotspotBox.length) {
            this.hotspotBox = this.hotspotBox.map((e, index) => {
                if (!e.name) {
                    e.name = `Choice ${index + 1}`
                }

                return e
            })
        }

        this.$nextTick(() => {
            this.fitStageIntoParentContainer(this.stageSize)
            if (this.hotspotBox.length && !this.isMobile) {
                this.setTooltipCursor()
            }
        })
    },

    mounted() {
        this.fitStageIntoParentContainer(this.stageSize)

        this.idCount = this.hotspotBox.length

        while (this.$refs[`box-${this.idCount}`]) {
            this.idCount += 1
        }

        window.addEventListener('resize', () => {
            this.fitStageIntoParentContainer(this.stageSize)
            this.checkIsMobile()
        })

        document.addEventListener('click', e => {
            if (
                e.target.nodeName == 'CANVAS' ||
                e.target.classList.contains('hotspot-tool-box') ||
                e.target.classList.contains('hotspot-check-icon') ||
                e.target.classList.contains('hotspot-trash-icon')
            )
                return

            this.selectedShapeName = ''
            this.updateTransformer()
        })

        document.addEventListener('touchstart', e => {
            if (
                e.target.nodeName == 'CANVAS' ||
                e.target.classList.contains('hotspot-tool-box') ||
                e.target.classList.contains('hotspot-check-icon') ||
                e.target.classList.contains('hotspot-trash-icon')
            )
                return

            this.selectedShapeName = ''
            this.updateTransformer()
        })
    },

    computed: {
        selectedData() {
            const select = this.hotspotBox.find(e => e.rectangles.name === this.selectedShapeName)

            if (select) {
                this.allowSwiper(false)
            } else {
                this.allowSwiper(true)
            }

            return select
        }
    },

    watch: {
        'question.image'() {
            this.renderCanvasImage()
        },
        hotspotBox: {
            handler(data) {
                this.$emit('hotspot-box', data)
            },
            deep: true
        }
    },

    methods: {
        addHotspotBox(type) {
            if (this.hotspotBox.length >= 15) {
                if (typeof process.env.VUE_APP_IS_BACKEND !== 'undefined') {
                    return window.shout.error('The maximum number of boxes is 15 boxes')
                }

                return popup.alert(this.$t('question_alert_limit_hotspot_box'))
            }

            let num = this.idCount

            if (this.removeList.length) {
                num = Math.min(...this.removeList)
                this.removeList.splice(
                    this.removeList.findIndex(e => e == num),
                    1
                )
            }

            this.hotspotBox.push({
                id: `box-${this.idCount}`,
                is_correct: false,
                name: `Choice ${num}`,
                type: type,
                rectangles: {
                    x: 0,
                    y: 0,
                    width: 100,
                    height: 100,
                    scaleX: 1,
                    scaleY: 1,
                    stroke: '#000',
                    strokeWidth: 1,
                    radius: type == 'circle' ? 50 : 0,
                    fill: 'rgba(128, 128, 128, .3)',
                    strokeScaleEnabled: false,
                    name: `rect${this.idCount}`,
                    draggable: false
                },
                group: {
                    x: 50,
                    y: 50,
                    draggable: true
                }
            })

            this.sortChoice()

            this.$nextTick(() => {
                do {
                    this.idCount += 1
                } while (this.$refs[`box-${this.idCount}`])

                if (!this.isMobile) {
                    this.setTooltipCursor()
                }

                this.$emit('add-hotspot-box')
            })
        },

        removeHotspotBox(id) {
            this.hotspotBox.splice(
                this.hotspotBox.findIndex(e => e.id === id),
                1
            )
            this.sortChoice()

            id = id.split('-')
            this.removeList.push(+id[1])

            if (this.isMobile) {
                this.selectedShapeName = ''
                this.updateTransformer()
            }
        },

        sortChoice() {
            this.hotspotBox = this.hotspotBox.map((e, index) => {
                return {
                    ...e,
                    name: `Choice ${index + 1}`
                }
            })
        },

        selectCorrect(item) {
            if (!item.is_correct) {
                item.is_correct = true
                item.rectangles.fill = 'rgba(40, 167, 69, .3)'
                item.rectangles.stroke = 'green'
            } else {
                item.is_correct = false
                item.rectangles.fill = 'rgba(128, 128, 128, .3)'
                item.rectangles.stroke = '#000'
            }
        },

        tooltipDisable(bool) {
            if (bool) {
                for (let i = 0; i < this.hotspotBox.length; i++) {
                    const bin = this.$refs[`${this.hotspotBox[i].id}-bin`][0].getNode()
                    const check = this.$refs[`${this.hotspotBox[i].id}-check`][0].getNode()

                    bin.setOpacity(0.3)
                    check.setOpacity(0.3)
                }
            } else {
                for (let i = 0; i < this.hotspotBox.length; i++) {
                    const bin = this.$refs[`${this.hotspotBox[i].id}-bin`][0].getNode()
                    const check = this.$refs[`${this.hotspotBox[i].id}-check`][0].getNode()

                    bin.setOpacity(1)
                    check.setOpacity(1)
                }
            }
        },

        handleDrag() {
            const rect = this.hotspotBox.find(r => r.rectangles.name === this.selectedShapeName)

            if (!rect) return

            const tr = this.$refs.transformer.getNode()
            const stage = tr.getStage()
            const boxes = tr.nodes().map(node => node.getClientRect())
            const box = this.getTotalBox(boxes)

            // where are shapes inside bounding box of all shapes?
            const offsetX = box.x - this.$refs[rect.id][0].getNode().x()
            const offsetY = box.y - this.$refs[rect.id][0].getNode().y()

            if (box.x < 0) {
                this.$refs[rect.id][0].getNode().setX(-offsetX)
            }
            if (box.y < 0) {
                this.$refs[rect.id][0].getNode().setY(-offsetY)
            }
            if (box.x + box.width > stage.width()) {
                this.$refs[rect.id][0].getNode().setX(stage.width() - box.width - offsetX)
            }
            if (box.y + box.height > stage.height()) {
                this.$refs[rect.id][0].getNode().setY(stage.height() - box.height - offsetY)
            }

            rect.group.x = this.$refs[rect.id][0].getNode().x()
            rect.group.y = this.$refs[rect.id][0].getNode().y()
        },

        allowSwiper(bool) {
            this.$emit('allow-swiper', bool)
        },

        getTotalBox(boxes) {
            let minX = Infinity
            let minY = Infinity
            let maxX = -Infinity
            let maxY = -Infinity

            boxes.forEach(box => {
                minX = Math.min(minX, box.x)
                minY = Math.min(minY, box.y)
                maxX = Math.max(maxX, box.x + box.width)
                maxY = Math.max(maxY, box.y + box.height)
            })
            return {
                x: minX,
                y: minY,
                width: maxX - minX,
                height: maxY - minY
            }
        },

        handleTransform(e) {
            const rect = this.hotspotBox.find(r => r.rectangles.name === this.selectedShapeName)

            // update the state
            rect.rectangles.x = e.target.x()
            rect.rectangles.y = e.target.y()
            rect.rectangles.rotation = e.target.rotation()
            rect.rectangles.scaleX = e.target.scaleX()
            rect.rectangles.scaleY = e.target.scaleY()

            if (!this.isMobile) {
                this.tooltipDisable(true)
            }
        },
        handleStageMouseDown(e) {
            // filter native events
            if (!e.evt) {
                return
            }

            // clicked on stage - clear selection
            if (e.target === e.target.getStage()) {
                this.selectedShapeName = ''
                this.updateTransformer()

                return
            }

            // clicked on transformer - do nothing
            const clickedOnTransformer = e.target.getParent().className === 'Transformer'
            if (clickedOnTransformer) {
                return
            }

            // find clicked rect by its name
            const name = e.target.name()

            const rect = this.hotspotBox.find(r => r.rectangles.name === name)

            if (rect) {
                this.selectedShapeName = name
            } else {
                this.selectedShapeName = ''
            }

            this.updateTransformer()
        },

        getCorner(pivotX, pivotY, diffX, diffY, angle) {
            const distance = Math.sqrt(diffX * diffX + diffY * diffY)

            /// find angle from pivot to corner
            angle += Math.atan2(diffY, diffX)

            /// get new x and y and round it off to integer
            const x = pivotX + distance * Math.cos(angle)
            const y = pivotY + distance * Math.sin(angle)

            return { x: x, y: y }
        },

        getClientRect(rotatedBox) {
            const { x, y, width, height } = rotatedBox
            const rad = rotatedBox.rotation

            const p1 = this.getCorner(x, y, 0, 0, rad)
            const p2 = this.getCorner(x, y, width, 0, rad)
            const p3 = this.getCorner(x, y, width, height, rad)
            const p4 = this.getCorner(x, y, 0, height, rad)

            const minX = Math.min(p1.x, p2.x, p3.x, p4.x)
            const minY = Math.min(p1.y, p2.y, p3.y, p4.y)
            const maxX = Math.max(p1.x, p2.x, p3.x, p4.x)
            const maxY = Math.max(p1.y, p2.y, p3.y, p4.y)

            return {
                x: minX,
                y: minY,
                width: maxX - minX,
                height: maxY - minY
            }
        },

        boundBoxFunction(oldBox, newBox) {
            const minWidth = 50
            const minHeight = 30
            const stage = this.$refs.transformer.getNode().getStage()
            const box = this.getClientRect(newBox)
            const isOut = box.x + box.width > stage.width() || box.y + box.height > stage.height()

            // if new bounding box is out of visible viewport, let's just skip transforming
            // this logic can be improved by still allow some transforming if we have small available space
            if (isOut || Math.abs(newBox.width) < minWidth || Math.abs(newBox.height) < minHeight) {
                return oldBox
            }

            return newBox
        },

        updateTransformer() {
            if (!this.$refs.transformer) return
            // here we need to manually attach or detach Transformer node
            const transformerNode = this.$refs.transformer.getNode()
            const stage = transformerNode.getStage()
            const { selectedShapeName } = this

            const selectedNode = stage.findOne('.' + selectedShapeName)
            // do nothing if selected node is already attached
            if (selectedNode === transformerNode.node()) {
                return
            }

            transformerNode.ignoreStroke(true)

            if (selectedNode) {
                // attach to another node
                transformerNode.nodes([selectedNode])
            } else {
                // remove transformer
                transformerNode.nodes([])
            }
        },

        renderCanvasImage() {
            const image = new window.Image()
            const checkImage = new window.Image()
            const unCheckImage = new window.Image()
            const binImage = new window.Image()
            image.crossOrigin = '*'
            image.src = this.question.image
            checkImage.src = `${baseUrl}images/check.png`
            unCheckImage.src = `${baseUrl}images/uncheck.png`
            binImage.src = `${baseUrl}images/bin.png`

            image.onload = async () => {
                this.image = await this.renderHotspotImage(image)
            }

            checkImage.onload = () => {
                this.checkedImage = checkImage
            }

            unCheckImage.onload = () => {
                this.unCheckedImage = unCheckImage
            }

            binImage.onload = () => {
                this.binImage = binImage
            }
        },

        setTooltipCursor() {
            for (let i = 0; i < this.hotspotBox.length; i++) {
                const bin = this.$refs[`${this.hotspotBox[i].id}-bin`][0].getNode()
                const check = this.$refs[`${this.hotspotBox[i].id}-check`][0].getNode()
                const container = this.$refs.stage.getStage().container()

                bin.on('mouseenter', () => {
                    container.style.cursor = 'pointer'
                })

                bin.on('mouseleave', () => {
                    container.style.cursor = 'default'
                })

                check.on('mouseenter', () => {
                    container.style.cursor = 'pointer'
                })

                check.on('mouseleave', () => {
                    container.style.cursor = 'default'
                })
            }
        },

        checkIsMobile() {
            this.isMobile = window.innerWidth < 1200 ? true : false
        },

        removeImage() {
            this.selectedShapeName = ''
            this.$nextTick(() => {
                this.question.answers = []
                this.idCount = 0
                this.$emit('remove-image')
            })
        }
    }
}
</script>

<style lang="scss">
.hotspot-select-screen-box {
    position: relative;
    max-width: 500px;
    width: 100%;
    height: auto;
    margin: 1rem auto;
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;

    @media screen and (max-width: 1199px) {
        margin: 48px auto 0 auto;
    }

    // @media screen and (max-width: 1199px) {
    //     margin: 64px auto 0 auto;
    // }

    // @media screen and (max-width: 767px) {
    //     margin: 64px auto 150px auto;
    // }

    .hotspot-tool {
        position: absolute;
        top: 0px;
        right: -36px;
        row-gap: 6px;
        z-index: 999;
        transition: opacity 0.3s;

        &.disabled {
            opacity: 0.1;
            pointer-events: none;
        }

        .hotspot-tool-box {
            width: 30px;
            height: 30px;
            border-radius: 50%;
            color: #fff;
            background-color: #808080;
            box-shadow: 0 0 2px rgba(0, 0, 0, 0.5);

            &.btn-dark {
                background-color: #000;
            }

            &.btn-danger {
                background-color: #dc3545;
            }

            &.btn-success {
                background-color: #28a745;
            }

            .rect-icon {
                width: 14px;
                height: 14px;
                background-color: #fff;
            }

            .circle-icon {
                width: 14px;
                height: 14px;
                border-radius: 50%;
                background-color: #fff;
            }
        }
    }

    .hotspot-select-screen {
        width: 100%;
        height: 100%;
        border-radius: 4px;
        aspect-ratio: 1 / 1;
    }
}
</style>
